import { useState } from "react";
import {
  Box,
  FormControlLabel,
  TextField as MuiTextField,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";

import { dayjs } from "@thalamos/common";
import type { RegisterRow } from "@thalamos/aspire-common";
import {
  CapacityOutcome,
  DeprivationOutcome,
  DolsTrackingStatus,
} from "@thalamos/aspire-common";

import { CapacityStatusPill } from "./CareHomeRegister.js";
import {
  Button,
  Dropdown,
  PopupDialog,
  PopupDialogTitle,
} from "~/components/design-system/index.js";

export type RecordCapacityStatusDialogProps = {
  row: RegisterRow;
  onClose: () => void;
  onSubmit: (args: {
    capacityOutcome: CapacityOutcome;
    deprivationOutcome: DeprivationOutcome;
    dolsStatus?: DolsTrackingStatus;
    reviewDate?: string;
    reason: string;
  }) => Promise<void>;
};

const reasons = [
  {
    value: "predates-emha",
    label: "Authorisation predates eMHA — paper record held",
  },
  { value: "assessed-offline", label: "Assessment completed outside eMHA" },
  { value: "correcting-record", label: "Correcting a previous entry" },
];

/**
 * Answers the two axes; the status is derived and shown back rather than chosen. That
 * is what makes a state contradicting its own answers unrepresentable — you cannot
 * record "consenting with capacity" alongside a deprivation of liberty.
 */
export function RecordCapacityStatusDialog(
  props: RecordCapacityStatusDialogProps,
) {
  const existing = props.row.capacityState.state;

  const [capacityOutcome, setCapacityOutcome] = useState<CapacityOutcome>(
    existing.capacity.outcome,
  );
  const [deprivationOutcome, setDeprivationOutcome] =
    useState<DeprivationOutcome>(existing.deprivation.outcome);
  const [dolsStatus, setDolsStatus] = useState<DolsTrackingStatus | undefined>(
    existing.dols?.status,
  );
  const [reviewDate, setReviewDate] = useState<string>(
    existing.dols?.expiryDateTime
      ? dayjs(existing.dols.expiryDateTime).format("YYYY-MM-DD")
      : "",
  );
  const [reason, setReason] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const dolsApplies =
    capacityOutcome === CapacityOutcome.LacksCapacity &&
    deprivationOutcome === DeprivationOutcome.Deprived;

  const reviewDateRequired =
    dolsApplies &&
    dolsStatus === DolsTrackingStatus.StandardAuthorisationGranted;

  const canSubmit =
    !!reason && (!reviewDateRequired || !!reviewDate) && !isSaving;

  const derivedState = {
    ...existing,
    capacity: { outcome: capacityOutcome },
    deprivation: { outcome: deprivationOutcome },
  };

  return (
    <PopupDialog open onClose={props.onClose}>
      <PopupDialogTitle
        closeDialog={props.onClose}
        titleText="Record capacity status"
      />
      <Typography sx={{ fontSize: "0.8rem", color: "#54596B", mb: 2 }}>
        {`${props.row.patientInfo.name.family.toUpperCase()}, ${props.row.patientInfo.name.given} · ${props.row.careHomeName}`}
      </Typography>

      <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
        <Box sx={{ flex: 1, minWidth: "14rem" }}>
          <Typography sx={{ fontWeight: 700, fontSize: "0.8rem", mb: 0.5 }}>
            Capacity to consent
          </Typography>
          <RadioGroup
            value={capacityOutcome}
            onChange={(e) => {
              setCapacityOutcome(e.target.value as CapacityOutcome);
            }}
          >
            <FormControlLabel
              value={CapacityOutcome.NotAssessed}
              control={<Radio size="small" />}
              label="Not assessed"
            />
            <FormControlLabel
              value={CapacityOutcome.LacksCapacity}
              control={<Radio size="small" />}
              label="Lacks capacity"
            />
            <FormControlLabel
              value={CapacityOutcome.HasCapacity}
              control={<Radio size="small" />}
              label="Has capacity"
            />
          </RadioGroup>
        </Box>

        <Box sx={{ flex: 1, minWidth: "14rem" }}>
          <Typography sx={{ fontWeight: 700, fontSize: "0.8rem", mb: 0.5 }}>
            Is this a deprivation of liberty?
          </Typography>
          <RadioGroup
            value={deprivationOutcome}
            onChange={(e) => {
              setDeprivationOutcome(e.target.value as DeprivationOutcome);
            }}
          >
            <FormControlLabel
              value={DeprivationOutcome.NotAssessed}
              control={<Radio size="small" />}
              label="Not assessed"
            />
            <FormControlLabel
              value={DeprivationOutcome.NotDeprived}
              control={<Radio size="small" />}
              label="No"
            />
            <FormControlLabel
              value={DeprivationOutcome.Deprived}
              control={<Radio size="small" />}
              label="Yes"
            />
          </RadioGroup>
        </Box>
      </Box>

      <Box
        data-testid="derived-status"
        sx={{
          border: "1px solid #2857A4",
          backgroundColor: "#F4F8FD",
          borderRadius: "4px",
          padding: "0.8em 1em",
          marginTop: "1em",
        }}
      >
        <Typography
          sx={{
            fontSize: "0.62rem",
            letterSpacing: "0.1em",
            color: "#2857A4",
            mb: 0.7,
          }}
        >
          RESULTING STATUS
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <CapacityStatusPill state={derivedState} />
          <Typography sx={{ fontSize: "0.72rem", color: "#54596B" }}>
            Derived from the two answers above — not chosen directly
          </Typography>
        </Box>
      </Box>

      {dolsApplies && (
        <Box sx={{ marginTop: "1em" }}>
          <Dropdown
            label="Referral stage"
            name="dolsStatus"
            renderedValue={dolsStatus ?? "-"}
            selectedValue={dolsStatus ?? ""}
            values={Object.values(DolsTrackingStatus).map((value) => ({
              value,
              label: value,
            }))}
            onChange={(value) => {
              setDolsStatus(value as DolsTrackingStatus);
            }}
          />
          {reviewDateRequired && (
            <Box sx={{ marginTop: "0.8em" }}>
              <MuiTextField
                fullWidth
                type="date"
                size="small"
                name="reviewDate"
                label="Review by"
                value={reviewDate}
                onChange={(e) => {
                  setReviewDate(e.target.value);
                }}
              />
              <Typography sx={{ fontSize: "0.72rem", color: "#54596B" }}>
                A standard authorisation may not exceed 12 months.
              </Typography>
            </Box>
          )}
        </Box>
      )}

      <Box sx={{ marginTop: "1em" }}>
        <Dropdown
          label="Reason for recording manually"
          name="reason"
          renderedValue={
            reasons.find((r) => r.value === reason)?.label ?? "Select a reason"
          }
          selectedValue={reason ?? ""}
          values={reasons}
          onChange={setReason}
        />
        <Typography sx={{ fontSize: "0.72rem", color: "#54596B" }}>
          Recorded as an assertion, not an assessment. The resident record shows
          which it was.
        </Typography>
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          marginTop: "1.5em",
        }}
      >
        <Button label="Cancel" color="error" onClick={props.onClose} />
        <Button
          label="Record status"
          disabled={!canSubmit}
          onClick={async () => {
            setIsSaving(true);
            try {
              await props.onSubmit({
                capacityOutcome,
                deprivationOutcome,
                dolsStatus: dolsApplies ? dolsStatus : undefined,
                reviewDate: reviewDateRequired ? reviewDate : undefined,
                reason:
                  reasons.find((r) => r.value === reason)?.label ?? reason!,
              });
            } finally {
              setIsSaving(false);
            }
          }}
        />
      </Box>
    </PopupDialog>
  );
}
